import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import {aiProviders} from '../../Common/aiProviders';
import {
    appendFieldError,
    createDropdown,
    createField,
    createInputField
} from '../forms/formFields';
import {cloneValue} from '../forms/formState';
import {stringifyJsonFallbackValue} from '../forms/jsonFallbackField';

const isPlainObject = value => Object.prototype.toString.call(value) === '[object Object]';
const hasOwnProperty = (value, property) => Object.prototype.hasOwnProperty.call(value, property);

const defaultChatModel = {
    credentials: '',
    provider: '',
    model: '',
    endpoint: '',
    temperature: null,
    topP: null,
    topK: null,
    responseJSONSchema: null
};

const normalizeString = value => typeof value === 'undefined' || value === null ? '' : String(value);

const normalizeNumber = value => {
    if (typeof value === 'undefined' || value === null || value === '') {
        return null;
    }

    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? value : numberValue;
};

const normalizeInteger = value => {
    if (typeof value === 'undefined' || value === null || value === '') {
        return null;
    }

    const numberValue = Number.parseInt(value, 10);
    return Number.isNaN(numberValue) ? value : numberValue;
};

const normalizeChatModel = chatModel => {
    if (!isPlainObject(chatModel)) {
        return {};
    }

    const normalizedChatModel = cloneValue(chatModel);
    ['credentials', 'provider', 'model', 'endpoint'].forEach(fieldName => {
        if (hasOwnProperty(chatModel, fieldName)) {
            normalizedChatModel[fieldName] = normalizeString(chatModel[fieldName]);
        }
    });

    ['temperature', 'topP'].forEach(fieldName => {
        if (hasOwnProperty(chatModel, fieldName)) {
            normalizedChatModel[fieldName] = normalizeNumber(chatModel[fieldName]);
        }
    });

    if (hasOwnProperty(chatModel, 'topK')) {
        normalizedChatModel.topK = normalizeInteger(chatModel.topK);
    }

    if (hasOwnProperty(chatModel, 'responseJSONSchema')) {
        normalizedChatModel.responseJSONSchema = cloneValue(chatModel.responseJSONSchema);
    }

    return normalizedChatModel;
};

const normalizeChatModelPatch = (chatModel, patch) => {
    const normalizedPatch = cloneValue(patch || {});

    Object.keys(normalizedPatch).forEach(fieldName => {
        const value = normalizedPatch[fieldName];
        if (value !== null && value !== '') {
            return;
        }

        if (!hasOwnProperty(chatModel, fieldName)) {
            delete normalizedPatch[fieldName];
        }
    });

    return normalizedPatch;
};

const getResource = (mxResources, key) => mxResources.get(key);

const fieldValue = (chatModel, fieldName) => normalizeString(chatModel[fieldName]);

const normalizeListedModels = models => {
    if (!Array.isArray(models)) {
        return [];
    }

    const normalizedModels = new Map();
    models.forEach(model => {
        const id = normalizeString(model?.id).trim();
        if (id.length === 0 || normalizedModels.has(id)) {
            return;
        }

        const name = normalizeString(model?.name).trim();
        const label = normalizeString(model?.label).trim();
        normalizedModels.set(id, {
            id,
            name,
            label: label || name || id
        });
    });
    return [...normalizedModels.values()];
};

const modelOptions = (selectedValue, listedModels) => {
    const options = new Map();
    const normalizedSelectedValue = normalizeString(selectedValue).trim();
    if (normalizedSelectedValue.length > 0) {
        options.set(normalizedSelectedValue, {
            id: normalizedSelectedValue,
            name: '',
            label: normalizedSelectedValue
        });
    }
    listedModels.forEach(model => options.set(model.id, model));
    return [...options.values()];
};

const modelDropdownOptions = (mxResources, selectedValue, listedModels) => [{
    value: '',
    name: getResource(mxResources, 'modelSelect')
}, ...modelOptions(selectedValue, listedModels).map(model => ({
    value: model.id,
    name: model.label || model.id
}))];

const createModelOption = (model, selectedValue) => {
    const option = document.createElement('option');
    option.value = model.id;
    option.textContent = model.label || model.id;
    if (model.id === selectedValue) {
        option.selected = true;
        option.setAttribute('selected', '');
    }
    return option;
};

const modelListingSourceFields = new Set(['credentials', 'provider', 'endpoint']);

const hasModelListingSourceChange = patch => Object.keys(patch || {})
    .some(fieldName => modelListingSourceFields.has(fieldName));

const numberFieldValue = (chatModel, fieldName) => {
    const value = chatModel[fieldName];
    return typeof value === 'undefined' || value === null ? '' : String(value);
};

const createCredentialOptions = (mxResources, value) => {
    const options = [{
        value: '',
        name: getResource(mxResources, 'credentialsSelect')
    }];

    if (!isEmpty(value)) {
        options.push({
            value,
            name: value
        });
    }

    return options;
};

const createProviderOptions = mxResources => [{
    value: '',
    name: getResource(mxResources, 'aiProviderSelect')
}, ...Object.entries(aiProviders).map(([value, provider]) => ({
    value,
    name: provider.name
}))];

const syncDropdownValue = (select, value) => {
    select.value = value || '';

    try {
        const jq = typeof $ !== 'undefined' ? $ : null;
        jq?.(select).dropdown('set selected', value || '');
    } catch (e) {
        select.value = value || '';
    }
};

const parseJsonObjectField = value => {
    if (isEmpty(value)) {
        return {
            valid: true,
            value: null
        };
    }

    try {
        const parsed = JSON.parse(value);
        return {
            valid: parsed === null || isPlainObject(parsed),
            value: parsed
        };
    } catch (e) {
        return {
            valid: false,
            value: null
        };
    }
};

const isAzureOpenAIProvider = provider => provider === 'AzureOpenAI';

const removeClassName = (element, className) => {
    element.className = element.className
        .split(/\s+/)
        .filter(currentClassName => currentClassName && currentClassName !== className)
        .join(' ');
};

const createChatModelForm = ({
                                 chatModel = {},
                                 chatModelErrors = {},
                                 editable = true,
                                 mxResources,
                                 mountElement = null,
                                 createActionField = null,
                                 modelListingCache = {},
                                 onRender = () => {},
                                 onChange = () => {},
                                 onProviderChange = () => {}
                             } = {}) => {
    let currentChatModel = normalizeChatModel(chatModel);
    let listedModels = normalizeListedModels(modelListingCache.models);
    let modelsListed = Array.isArray(modelListingCache.models);
    let modelListingError = normalizeString(modelListingCache.error);
    let responseJSONSchemaValid = true;
    let responseJSONSchemaError = null;
    let responseJSONSchemaEditor = null;
    let responseJSONSchemaCodeEditorModule = null;

    const container = mountElement || document.createElement('div');
    container.innerHTML = '';
    if (!mountElement) {
        container.className = 'ui form chat-model-form';
    }

    if (typeof chatModelErrors === 'string') {
        const chatModelError = document.createElement('div');
        chatModelError.className = 'ui basic error large pointing prompt label chat-model-error';
        chatModelError.textContent = chatModelErrors;
        container.appendChild(chatModelError);
    }

    const credentialsField = createDropdown({
        label: getResource(mxResources, 'credentials'),
        id: 'chatModelCredentials',
        name: 'chatModel.credentials',
        value: fieldValue(currentChatModel, 'credentials'),
        className: 'required',
        clearable: true,
        searchable: true,
        error: chatModelErrors?.credentials,
        options: createCredentialOptions(mxResources, fieldValue(currentChatModel, 'credentials'))
    });
    const credentialsSelect = credentialsField.querySelector('select');
    credentialsSelect.className += ' credentials';
    credentialsSelect.value = fieldValue(currentChatModel, 'credentials');
    credentialsSelect.addEventListener('change', event => emitChatModel({credentials: event.target.value}));
    container.appendChild(credentialsField);

    const providerField = createDropdown({
        label: getResource(mxResources, 'aiProvider'),
        id: 'chatModelProvider',
        name: 'chatModel.provider',
        value: fieldValue(currentChatModel, 'provider'),
        className: 'required',
        searchable: true,
        error: chatModelErrors?.provider,
        options: createProviderOptions(mxResources)
    });
    const providerSelect = providerField.querySelector('select');
    providerSelect.value = fieldValue(currentChatModel, 'provider');
    providerSelect.addEventListener('change', event => {
        const patch = {provider: event.target.value};
        if (!isAzureOpenAIProvider(event.target.value)) {
            patch.endpoint = '';
        }
        emitChatModel(patch);
        renderEndpointField();
        onProviderChange(event.target.value);
    });
    container.appendChild(providerField);

    const modelField = createDropdown({
        label: getResource(mxResources, 'model'),
        id: 'chatModelModel',
        name: 'chatModel.model',
        value: fieldValue(currentChatModel, 'model'),
        className: 'required',
        searchable: true,
        allowAdditions: true,
        help: getResource(mxResources, 'modelHelp'),
        error: chatModelErrors?.model,
        options: modelDropdownOptions(mxResources, fieldValue(currentChatModel, 'model'), listedModels)
    });
    const modelDropdown = modelField.querySelector('select');
    modelDropdown.className += ` chat-model-dropdown${editable ? '' : ' disabled'}`;
    modelDropdown.addEventListener('change', event => {
        if (!modelDropdown.disabled && !modelDropdown.readOnly) {
            emitChatModel({model: event.target.value});
        }
    });
    modelDropdown.addEventListener('chat-models-listed', event => {
        listedModels = normalizeListedModels(event.detail?.models);
        modelsListed = true;
        modelListingError = '';
        if (event.detail?.render === false) {
            syncModelListingFeedback();
        } else {
            syncModelDropdown();
        }
    });
    modelDropdown.addEventListener('chat-models-listing-error', event => {
        modelListingError = normalizeString(event.detail?.message);
        listedModels = [];
        modelsListed = false;
        if (event.detail?.render === false) {
            syncModelListingFeedback();
        } else {
            syncModelDropdown();
        }
    });
    container.appendChild(modelField);

    let endpointField = null;
    let endpointInput = null;

    const temperatureField = createInputField({
        label: getResource(mxResources, 'temperature'),
        id: 'chatModelTemperature',
        name: 'chatModel.temperature',
        type: 'number',
        step: 'any',
        value: numberFieldValue(currentChatModel, 'temperature'),
        help: getResource(mxResources, 'temperatureHelp'),
        error: chatModelErrors?.temperature
    });
    const temperatureInput = temperatureField.querySelector('input');
    temperatureInput.addEventListener('change', event => emitChatModel({temperature: normalizeNumber(event.target.value)}));
    container.appendChild(temperatureField);

    const topPField = createInputField({
        label: getResource(mxResources, 'topP'),
        id: 'chatModelTopP',
        name: 'chatModel.topP',
        type: 'number',
        step: 'any',
        value: numberFieldValue(currentChatModel, 'topP'),
        help: getResource(mxResources, 'topPHelp'),
        error: chatModelErrors?.topP
    });
    const topPInput = topPField.querySelector('input');
    topPInput.addEventListener('change', event => emitChatModel({topP: normalizeNumber(event.target.value)}));
    container.appendChild(topPField);

    const topKField = createInputField({
        label: getResource(mxResources, 'topK'),
        id: 'chatModelTopK',
        name: 'chatModel.topK',
        type: 'number',
        min: 1,
        value: numberFieldValue(currentChatModel, 'topK'),
        help: getResource(mxResources, 'topKHelp'),
        error: chatModelErrors?.topK
    });
    const topKInput = topKField.querySelector('input');
    topKInput.addEventListener('change', event => emitChatModel({topK: normalizeInteger(event.target.value)}));
    container.appendChild(topKField);

    const responseJSONSchemaEditorMount = document.createElement('div');
    responseJSONSchemaEditorMount.id = 'chatModelResponseJSONSchemaEditor';
    responseJSONSchemaEditorMount.className = 'ui active centered inline loader';
    const responseJSONSchemaField = createField({
        label: getResource(mxResources, 'responseJSONSchema'),
        error: chatModelErrors?.responseJSONSchema,
        fieldElement: responseJSONSchemaEditorMount
    });
    container.appendChild(responseJSONSchemaField);

    const actionField = createActionField?.();
    if (actionField) {
        container.appendChild(actionField);
    }

    const renderEndpointField = () => {
        endpointField?.remove();
        endpointField = null;
        endpointInput = null;

        if (!isAzureOpenAIProvider(fieldValue(currentChatModel, 'provider'))) {
            return;
        }

        endpointField = createInputField({
            label: getResource(mxResources, 'providerEndpoint'),
            id: 'chatModelEndpoint',
            name: 'chatModel.endpoint',
            className: 'required',
            type: 'text',
            value: fieldValue(currentChatModel, 'endpoint'),
            help: getResource(mxResources, 'providerEndpointHelp'),
            error: chatModelErrors?.endpoint
        });
        endpointInput = endpointField.querySelector('input');
        endpointInput.addEventListener('change', event => emitChatModel({endpoint: event.target.value}));
        container.insertBefore(endpointField, modelField);
    };

    const syncResponseJSONSchemaEditor = () => {
        responseJSONSchemaEditor?.setValue(stringifyJsonFallbackValue(currentChatModel.responseJSONSchema));
    };

    const syncResponseJSONSchemaError = () => {
        responseJSONSchemaField.querySelector('.chat-model-response-json-schema-error')?.remove();
        removeClassName(responseJSONSchemaField, 'error');

        if (!responseJSONSchemaError) {
            return;
        }

        responseJSONSchemaField.className += ' error';
        const errorPrompt = document.createElement('div');
        errorPrompt.className = 'ui basic error large pointing prompt label chat-model-response-json-schema-error';
        errorPrompt.textContent = responseJSONSchemaError;
        responseJSONSchemaField.appendChild(errorPrompt);
    };

    const syncStructuredFields = () => {
        credentialsSelect.value = fieldValue(currentChatModel, 'credentials');
        syncDropdownValue(credentialsSelect, fieldValue(currentChatModel, 'credentials'));
        providerSelect.value = fieldValue(currentChatModel, 'provider');
        syncDropdownValue(providerSelect, fieldValue(currentChatModel, 'provider'));
        syncModelDropdown();
        renderEndpointField();
        if (endpointInput) {
            endpointInput.value = fieldValue(currentChatModel, 'endpoint');
        }
        temperatureInput.value = numberFieldValue(currentChatModel, 'temperature');
        topPInput.value = numberFieldValue(currentChatModel, 'topP');
        topKInput.value = numberFieldValue(currentChatModel, 'topK');
        responseJSONSchemaValid = true;
        responseJSONSchemaError = null;
        syncResponseJSONSchemaError();
        syncResponseJSONSchemaEditor();
    };

    const syncModelDropdown = () => {
        const selectedValue = fieldValue(currentChatModel, 'model');
        modelDropdown.value = selectedValue;
        modelDropdown.dataset.chatModelCredentials = fieldValue(currentChatModel, 'credentials');
        modelDropdown.dataset.chatModelProvider = fieldValue(currentChatModel, 'provider');
        modelDropdown.dataset.chatModelEndpoint = fieldValue(currentChatModel, 'endpoint');
        if (modelsListed) {
            modelDropdown.dataset.chatModelSuggestionsLoaded = 'true';
        } else {
            delete modelDropdown.dataset.chatModelSuggestionsLoaded;
        }

        modelDropdown.innerHTML = '';
        const emptyOption = document.createElement('option');
        emptyOption.value = '';
        emptyOption.textContent = getResource(mxResources, 'modelSelect');
        if (!selectedValue) {
            emptyOption.selected = true;
            emptyOption.setAttribute('selected', '');
        }
        modelDropdown.appendChild(emptyOption);
        modelOptions(selectedValue, listedModels)
            .forEach(model => modelDropdown.appendChild(createModelOption(model, selectedValue)));
        modelDropdown.value = selectedValue;
        if (modelDropdown.dataset.chatModelDropdownInitialized === 'true' &&
            typeof $ === 'function') {
            $(modelDropdown).dropdown('refresh');
            syncDropdownValue(modelDropdown, selectedValue);
        }

        syncModelListingFeedback();
    };

    const syncModelListingFeedback = () => {
        modelField.querySelector('.chat-model-listing-error')?.remove();
        if (!modelField.querySelector('.prompt')) {
            removeClassName(modelField, 'error');
        }

        if (modelListingError) {
            appendFieldError(modelField, modelListingError, {
                className: 'chat-model-listing-error'
            });
        }
    };

    function emitChatModel(patch) {
        const normalizedPatch = normalizeChatModelPatch(currentChatModel, patch);
        const modelListingSourceChanged = hasModelListingSourceChange(normalizedPatch);
        const nextChatModel = normalizeChatModel({
            ...currentChatModel,
            ...normalizedPatch
        });
        if (isEqual(currentChatModel, nextChatModel)) {
            return;
        }

        currentChatModel = nextChatModel;
        if (modelListingSourceChanged) {
            listedModels = [];
            modelsListed = false;
            modelListingError = '';
            delete modelDropdown.dataset.chatModelDropdownInitialized;
            syncModelDropdown();
            onRender(container);
        }
        onChange(cloneValue(currentChatModel));
    }

    renderEndpointField();
    syncModelDropdown();
    syncResponseJSONSchemaError();

    return {
        element: container,
        getChatModel: () => cloneValue(currentChatModel),
        getModelListingCache: () => ({
            models: modelsListed ? cloneValue(listedModels) : null,
            error: modelListingError
        }),
        setChatModel: value => {
            currentChatModel = normalizeChatModel(value);
            listedModels = [];
            modelsListed = false;
            modelListingError = '';
            syncStructuredFields();
        },
        isValid: () => responseJSONSchemaValid,
        mountResponseJSONSchemaEditor: codeEditorModule => {
            if (responseJSONSchemaEditor || responseJSONSchemaCodeEditorModule) {
                return;
            }

            responseJSONSchemaCodeEditorModule = codeEditorModule;
            codeEditorModule.codeEditor({
                callbackFn: editor => {
                    responseJSONSchemaEditor = editor;
                    editor.on('blur', codeMirror => {
                        const value = codeMirror.getValue();
                        codeMirror.setOption('lint', !isEmpty(value));
                        if (isEmpty(value)) {
                            responseJSONSchemaValid = true;
                            responseJSONSchemaError = null;
                            syncResponseJSONSchemaError();
                            emitChatModel({responseJSONSchema: null});
                            return;
                        }

                        const getAnnotations = codeMirror.getHelper(responseJSONSchemaCodeEditorModule.CodeMirror.Pos(0, 0), 'lint');
                        const annotations = getAnnotations(value, codeMirror.state?.lint?.linterOptions, codeMirror);
                        const parsed = parseJsonObjectField(value);
                        if (parsed.valid && isEmpty(annotations)) {
                            responseJSONSchemaValid = true;
                            responseJSONSchemaError = null;
                            syncResponseJSONSchemaError();
                            emitChatModel({responseJSONSchema: parsed.value});
                            return;
                        }

                        responseJSONSchemaValid = false;
                        responseJSONSchemaError = getResource(mxResources, 'responseJSONSchemaInvalidJson');
                        syncResponseJSONSchemaError();
                    });

                    const wrapper = editor.getWrapperElement();
                    wrapper.classList.add('ui', 'segment');
                    responseJSONSchemaEditorMount.replaceWith(wrapper);
                    syncResponseJSONSchemaEditor();
                    editor.refresh();
                },
                errorFn: () => {
                    responseJSONSchemaEditorMount.className = 'ui message error visible';
                    responseJSONSchemaEditorMount.textContent = getResource(mxResources, 'errorLoadingEditor');
                },
                options: {
                    readOnly: !editable ? 'nocursor' : false,
                    mode: 'application/json',
                    lint: !isEmpty(stringifyJsonFallbackValue(currentChatModel.responseJSONSchema)),
                    value: stringifyJsonFallbackValue(currentChatModel.responseJSONSchema)
                }
            });
        }
    };
};

export {
    createChatModelForm,
    defaultChatModel,
    normalizeChatModel,
    normalizeChatModelPatch
};
